/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { type TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import type Store from '@ember-data/store';
import { inject as service } from '@ember/service';
import Ruleset from 'embercom/models/matching-system/ruleset';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import { tracked } from '@glimmer/tracking';
import { EVERYONE_PREDICATE } from 'embercom/lib/operator/custom-bots/constants';
import { action } from '@ember/object';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { type EmberCPValidationsResult } from 'embercom/validations/typescript/ember-cp-validations-compat';
import type HelpCenterSite from 'embercom/models/help-center-site';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import type ContentImportService from 'embercom/services/content-import-service';
import { isPresent } from '@ember/utils';
import StandardAudienceEditorConfiguration from 'embercom/objects/content-editor/panels/audience/standard-audience-editor-configuration';
import type { Router } from '@ember/routing';
import { validateSegmentWithRuleset } from 'embercom/validations/typescript/ai-content-segments/segment';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import type IntlService from 'ember-intl/services/intl';
import { get, post } from 'embercom/lib/ajax';
import { EntityType } from 'embercom/models/data/entity-types';
import {
  type AudienceSummaryItem,
  type AudienceItems,
  type BrandsFromRulesets,
  type AudienceItemCounts,
  type EverybodySegment,
} from './types';

const ROUTE_NAMES = {
  FIN_AI_AGENT_SETTINGS: 'apps.app.automation.fin-ai-agent.settings',
  FIN_AI_AGENT_SETTINGS_STANDALONE: 'apps.app.settings.ai-automation.fin-ai-agent',
  KNOWLEDGE_HUB: 'apps.app.knowledge-hub.all-content',
  FIN_AI_AGENT_GUIDANCE: 'apps.app.automation.fin-ai-agent.guidance',
  CUSTOM_ACTIONS: 'apps.app.settings.app-settings.custom-actions',
  WORKFLOWS_OVERVIEW: 'apps.app.automation.workflows-overview',
  FIN_AI_AGENT_TASKS: 'apps.app.automation.fin-ai-agent.tasks',
  FIN_AI_AGENT_CONTENT: 'apps.app.automation.fin-ai-agent.content',
} as const;

interface Args {}

export default class Editor extends Component<Args> {
  @service declare store: Store;
  @service declare appService: any;
  @service declare contentEditorService: any;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intercomEventService: any;
  @service declare helpCenterService: any;
  @service declare contentImportService: ContentImportService;
  @service declare permissionsService: $TSFixMe;
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare attributeService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;

  @tracked showSideDrawer = false;
  @tracked selectedRuleset?: any;
  @tracked selectedSectionId: string | undefined;
  @tracked segments: AiContentSegment[] = [];
  @tracked audienceItems: AudienceItems | undefined;
  @tracked brandIdentityData: BrandsFromRulesets | undefined;

  owner: unknown;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.owner = owner;
    this.segments = this.aiContentSegmentsService.segments.sortBy('createdAt').reverse();
    this.showSideDrawer = false;
    Promise.all([taskFor(this.fetchAudienceItems).perform(), this.fetchBrandsFromRulesets()]);
  }

  get audienceEditorConfiguration() {
    return new StandardAudienceEditorConfiguration({
      container: this.owner,
      showClientPredicates: false,
      showMatchBehavior: false,
      showSendToUnsubscribedToggle: false,
      showAudiencePreview: this.showAudiencePreview,
      showEventRules: false,
      showDateEvents: false,
      includeConversationAttributes: this.appService.app.canUseAudienceForCopilot,
      additionalAttributes: this.additionalAttributes,
      showAudiencePreviewHidden: !this.showAudiencePreview,
      audiencePreviewHiddenText: this.audiencePreviewHiddenText,
      showUnsupportedAttributesBanner: this.hasInvalidConversationPredicates,
      unsupportedAttributesBannerText: this.intl.t(
        'settings.audience.settings.invalid-conversation-predicates',
      ),
      useSupportedPredicateAttributes: !this.appService.app.canUseAudienceForCopilot,
    });
  }

  get segment(): AiContentSegment {
    return this.contentEditorService.activeObject as AiContentSegment;
  }

  get sources(): ContentImportSource[] {
    return this.contentImportService.contentImportSources ?? [];
  }

  get validations(): EmberCPValidationsResult {
    return validateSegmentWithRuleset(this.segment, this.selectedRuleset);
  }

  get helpCenters(): HelpCenterSite[] {
    return this.helpCenterService.allSites;
  }

  @action async exitEditMode() {
    this.contentEditorService.unregister(this.selectedRuleset);
    this.selectedRuleset = undefined;
    await this.reloadSegments();
    this.selectedSectionId = undefined;
  }

  private async reloadSegments(shouldUnload = true) {
    if (shouldUnload) {
      this.store.unloadAll('ai-content-segment');
    }
    let results = await this.store.findAll('ai-content-segment');

    await this.aiContentSegmentsService.refreshSegments();

    this.segments = results.toArray().sortBy('createdAt').reverse();
  }

  @action async onCancel() {
    if (this.selectedSectionId === 'default') {
      this.showSideDrawer = false;
      this.selectedSectionId = undefined;
      return;
    }

    if (this.contentEditorService.editorConfiguration) {
      await this.contentEditorService.cancelRulesetChanges.perform();
      this.showSideDrawer = false;
      await this.exitEditMode();
    }
  }

  @action async onSave() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    await this.contentEditorService.saveRuleset.perform();
    this.trackAnalyticsEvent('clicked', 'save_button', {
      brand_predicate_selected: this.rulesetContainsBrandPredicate,
    });
    await this.exitEditMode();
    await this.fetchBrandsFromRulesets();
    this.showSideDrawer = false;
  }

  @action async onDelete() {
    this.showSideDrawer = false;
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }
    let segment = this.segment;
    await this.contentEditorService.deleteRuleset.perform({ transitionRoute: false });
    segment.unloadRecord();
    await this.reloadImportServiceAndHelpCenter();
    await this.exitEditMode();
    this.trackAnalyticsEvent('clicked', 'delete_button');
  }

  private async reloadImportServiceAndHelpCenter() {
    await Promise.all([
      this.contentImportService.forceFetchContentImportSources(),
      this.helpCenterService.forceFetchSites(),
    ]);
  }

  get segmentsLoading() {
    return taskFor(this.aiContentSegmentsService.loadContentSegments).isRunning;
  }

  get isFinAudienceEnabledForDataConnectors() {
    return this.appService?.app?.canUseFeature?.('tdf-migrate-actions-to-fin-audience');
  }

  get isFinAudienceEnabledForWorkflows() {
    return this.appService?.app?.canUseFeature?.('workflows-fin-audiences');
  }

  get isLoading() {
    return taskFor(this.selectSegment).isRunning || taskFor(this.newSegment).isRunning;
  }

  get dataItemsLoading() {
    return taskFor(this.fetchAudienceItems).isRunning;
  }

  @restartableTask *selectSegment(
    segment: AiContentSegment | EverybodySegment,
  ): TaskGenerator<void> {
    this.selectedSectionId = segment.id;
    if (segment.id === 'default') {
      this.showSideDrawer = true;
      return;
    }

    if (isPresent(this.selectedRuleset)) {
      if (this.segmentAlreadySelected(segment)) {
        return;
      }
    }
    let selectedSegment;

    try {
      selectedSegment = yield this.store.findRecord('ai-content-segment', segment.id, {
        reload: true,
      });
    } catch (e) {
      this.notificationsService.notifyError(this.intl.t('settings.audience.segment-removed'));
      yield this.reloadSegments();
      this.selectedSectionId = undefined;
      return;
    }
    this.showSideDrawer = true;
    this.selectedRuleset = yield this.store.findRecord(
      'matching-system/ruleset',
      selectedSegment.rulesetId,
    );
    this.contentEditorService.register({
      ruleset: this.selectedRuleset,
      mode: 'edit',
    });
  }

  private getContentItemsCount(
    audienceItems: AudienceItemCounts | undefined,
    everybodyItems: AudienceItemCounts | undefined,
  ): number {
    let audienceWorkflowActions = this.isFinAudienceEnabledForDataConnectors
      ? audienceItems?.[EntityType.WorkflowConnectorAction] || 0
      : 0;
    let everybodyWorkflowActions = this.isFinAudienceEnabledForDataConnectors
      ? everybodyItems?.[EntityType.WorkflowConnectorAction] || 0
      : 0;

    return (
      (audienceItems?.total || 0) -
      (audienceItems?.[EntityType.Guideline] || 0) -
      audienceWorkflowActions +
      (everybodyItems?.total || 0) -
      (everybodyItems?.[EntityType.Guideline] || 0) -
      everybodyWorkflowActions
    );
  }

  private buildAudienceSummary(
    segment: AiContentSegment | EverybodySegment,
    audienceItems: AudienceItemCounts | undefined,
    everybodyItems: AudienceItemCounts | undefined,
  ): AudienceSummaryItem[] {
    let defaultBrand = this.brandIdentityData?.['default'];
    let summary: AudienceSummaryItem[] = [
      {
        label: this.intl.t('settings.audience.items-table.identity'),
        value:
          this.brandIdentityData?.[segment.rulesetId as keyof BrandsFromRulesets]?.identity?.name ||
          defaultBrand?.identity?.name,
        routeName: this.appService.app.canUseStandalone
          ? ROUTE_NAMES.FIN_AI_AGENT_SETTINGS_STANDALONE
          : ROUTE_NAMES.FIN_AI_AGENT_SETTINGS,
      },
      {
        label: this.intl.t('settings.audience.items-table.content'),
        value: this.intl.t('settings.audience.items-table.items', {
          count: this.getContentItemsCount(audienceItems, everybodyItems),
        }),
        routeName: ROUTE_NAMES.FIN_AI_AGENT_CONTENT,
      },
      {
        label: this.intl.t('settings.audience.items-table.guidance'),
        value: this.intl.t('settings.audience.items-table.items', {
          count:
            (everybodyItems?.[EntityType.Guideline] || 0) +
            (audienceItems?.[EntityType.Guideline] || 0),
        }),
        routeName: ROUTE_NAMES.FIN_AI_AGENT_GUIDANCE,
      },
    ];

    // Only show workflows and tasks if the workflows-fin-audiences feature flag is enabled
    if (this.isFinAudienceEnabledForWorkflows) {
      summary.push({
        label: this.intl.t('settings.audience.items-table.workflows'),
        value: this.intl.t('settings.audience.items-table.items', {
          count: audienceItems?.[EntityType.CustomBotSnapshot] || 0,
        }),
        routeName: ROUTE_NAMES.WORKFLOWS_OVERVIEW,
      });

      // Only show tasks if the AI Agent Workflows feature is also enabled
      if (this.appService.app.canUseAiAgentWorkflows) {
        summary.push({
          label: this.intl.t('settings.audience.items-table.tasks'),
          value: this.intl.t('settings.audience.items-table.items', {
            count: audienceItems?.[EntityType.AIAgentWorkflow] || 0,
          }),
          routeName: ROUTE_NAMES.FIN_AI_AGENT_TASKS,
        });
      }
    }

    if (this.isFinAudienceEnabledForDataConnectors) {
      summary.push({
        label: this.intl.t('settings.audience.items-table.data-connectors'),
        value: this.intl.t('settings.audience.items-table.items', {
          count:
            (everybodyItems?.[EntityType.WorkflowConnectorAction] || 0) +
            (audienceItems?.[EntityType.WorkflowConnectorAction] || 0),
        }),
        routeName: ROUTE_NAMES.CUSTOM_ACTIONS,
      });
    }

    return summary;
  }

  private segmentAlreadySelected(segment: AiContentSegment | EverybodySegment) {
    return segment.rulesetId === Number(this.selectedRuleset.id);
  }

  @restartableTask *newSegment(): TaskGenerator<void> {
    try {
      yield this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e) {
      return;
    }

    if (isPresent(this.selectedRuleset)) {
      yield this.onCancel();
    }

    let ruleset = yield Ruleset.createForType(this.store, {
      app_id: this.appService.app.id,
      object_type: objectTypes.aiContentSegment,
      match_behavior: matchBehaviors.transient,
      object_data: {},
      role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
    });
    this.showSideDrawer = true;
    this.selectedRuleset = ruleset;
    yield this.contentEditorService.register({
      ruleset: this.selectedRuleset,
      mode: 'edit',
    });
    yield this.reloadSegments(false);
    this.trackAnalyticsEvent('clicked', 'new_segment_button');
  }

  get rulesetHasConversationAttributes() {
    let predicates = this.selectedRuleset.predicateGroup.predicates.serialize();
    return this.filterForPredicates(predicates, ['conversation', 'brand']);
  }

  get showAudiencePreview() {
    return !this.appService.app.canUseStandalone && !this.rulesetHasConversationAttributes;
  }

  get audiencePreviewHiddenText() {
    return !this.appService.app.canUseStandalone
      ? this.intl.t('settings.audience.no-preview-conversation')
      : '';
  }

  @action
  segmentContainsConversationAttributes(segment: AiContentSegment) {
    if (segment.id === 'default') {
      return false;
    } else {
      return this.filterForPredicates(segment.predicateGroup?.predicates.serialize(), [
        'conversation',
        'brand',
      ]);
    }
  }

  get rulesetContainsBrandPredicate() {
    let predicates = this.selectedRuleset.predicateGroup.predicates.serialize();
    return this.filterForPredicates(predicates, ['brand']);
  }

  get hasInvalidConversationPredicates() {
    return !this.appService.app.canUseAudienceForCopilot && this.rulesetHasConversationAttributes;
  }

  get additionalAttributes() {
    if (this.appService.app.canUseAllAttributesInAIAudiences) {
      return this.attributeService.visualBotBuilderConditionalAttributes;
    }
    return [];
  }

  private filterForPredicates(predicates: $TSFixMe, attributeNames: string[] = []) {
    return predicates?.any((predicate: $TSFixMe) => {
      if (predicate.predicates) {
        return this.filterForPredicates(predicate.predicates, attributeNames);
      } else {
        return attributeNames.any((attribute) => predicate.attribute.startsWith(attribute));
      }
    });
  }

  @restartableTask *fetchAudienceItems(): TaskGenerator<void> {
    try {
      this.audienceItems = yield get(
        `/ember/ai_audiences/audience_items?app_id=${this.appService.app.id}`,
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('settings.audience.error.returning-audience-data'),
      );
      this.audienceItems = undefined;
    }
  }

  private async fetchBrandsFromRulesets() {
    try {
      let rulesetIds = this.segments.map((segment) => segment.rulesetId);
      this.brandIdentityData = await post(`/ember/ai_audiences/get_fin_identities_from_rulesets`, {
        app_id: this.appService.app.id,
        ids: rulesetIds,
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('settings.audience.error.returning-brand-data'),
      );
      this.brandIdentityData = undefined;
    }
  }

  get audienceSummaryColumns() {
    return [
      {
        label: '',
        valuePath: 'label',
        width: '30%',
      },
      {
        label: '',
        valuePath: 'value',
      },
    ];
  }

  /**
   * Redirects to the specified route with appropriate query parameters.
   * For Identity routes, adds 'personality-settings' section param.
   * For other routes, passes the selected audience segment ID for filtering.
   */
  @action
  redirectTo(redirectRoute: string) {
    let queryParams;
    if (
      redirectRoute === ROUTE_NAMES.FIN_AI_AGENT_SETTINGS ||
      redirectRoute === ROUTE_NAMES.FIN_AI_AGENT_SETTINGS_STANDALONE
    ) {
      queryParams = { section: 'personality-settings' };
    } else {
      queryParams = { aiContentSegmentIds: this.selectedSectionId };
    }
    this.router.transitionTo(redirectRoute, { queryParams });
  }

  private createDefaultSegment(): EverybodySegment {
    return {
      id: 'default',
      name: this.intl.t('settings.audience.everyone'),
      nameWithFallback: this.intl.t('settings.audience.everyone'),
      rulesetId: 'default',
      predicateGroup: { predicates: [] },
    };
  }

  get segmentsWithEverybody(): (AiContentSegment | EverybodySegment)[] {
    let everybodySegment = this.createDefaultSegment();
    return [everybodySegment, ...this.segments];
  }

  @action
  getAudienceItemTotal(segmentId: string | undefined): number {
    if (segmentId === 'default') {
      return this.audienceItems?.everybody?.total || 0;
    }
    return this.audienceItems?.[segmentId as keyof AudienceItems]?.total || 0;
  }

  @action
  getAgentName(rulesetId: string | number): string {
    let brandData = this.brandIdentityData?.[rulesetId as keyof BrandsFromRulesets];
    if (brandData) {
      return brandData.identity?.name || this.brandIdentityData?.default?.identity?.name || '';
    }
    return this.brandIdentityData?.default?.identity?.name || '';
  }

  @action
  getTotalItemsForSegment(segmentID: string | undefined): number {
    if (segmentID === 'default') {
      return this.audienceItems?.everybody?.total || 0;
    }

    let segmentTotal = this.getAudienceItemTotal(segmentID) || 0;
    let everybodyTotal = this.audienceItems?.everybody?.total || 0;

    return segmentTotal + everybodyTotal;
  }

  get selectedAudienceSummary(): AudienceSummaryItem[] {
    if (this.selectedSectionId === 'default') {
      let everybodyItems = this.audienceItems ? this.audienceItems['everybody'] : undefined;
      let everybodySegment = this.createDefaultSegment();
      return this.buildAudienceSummary(everybodySegment, undefined, everybodyItems);
    }
    if (!this.selectedRuleset || !this.segment) {
      return [];
    }

    let audienceItems = this.audienceItems?.[this.segment.id as keyof AudienceItems] || undefined;
    let everybodyItems = this.audienceItems ? this.audienceItems['everybody'] : undefined;

    return this.buildAudienceSummary(this.segment, audienceItems, everybodyItems);
  }

  private trackAnalyticsEvent(action: string, object: string, metadata?: any): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      section: 'operator',
      context: 'manage_audience_settings',
      place: 'settings',
      ...metadata,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Automation::FinAiAgent::Settings::Audience::Editor': typeof Editor;
  }
}
